import React, { FunctionComponent, useRef, useCallback, useState, useEffect } from 'react';
import { KeyPath } from 'app/models/entityStateSync/keypath';
import { useSurfaceState } from 'app/components/rtc/draw/DrawContext';
import { ISize } from 'app/components/layout/useMeasure';
import { Shape, Shapes } from 'app/models/rtc/draw/shape';
import { TraceType } from 'app/models/rtc/draw/trace';
import { useCss } from 'app/utils/css';

interface IProps {
  keyPath: KeyPath;
  /**
   * Should reflect the size of the underlying image in CSS pixels
   */
  size: ISize;
}

/**
 * Renders all shapes for the surface addressed by a keypath
 */
const DrawRender: FunctionComponent<IProps> = ({ keyPath, size }) => {
  const css = useCss();
  const surfaces = useSurfaceState(keyPath);
  const [canvasContext, setCanvasContext] = useState<CanvasRenderingContext2D | null>(null);

  const onCanvasRef = useCallback((canvas: HTMLCanvasElement | null) => {
    if (canvas) {
      setCanvasContext(canvas.getContext('2d'));
    }
  }, [setCanvasContext]);

  const { reset, render } = useDrawOnCanvas(canvasContext);

  // reset when keypath changes
  useEffect(() => {
    reset();
  }, [reset, keyPath]);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      render(surfaces.persisted, surfaces.pending);
    });
    return () => cancelAnimationFrame(id);
  }, [render, surfaces]);

  const isEmpty = (surfaces.persisted.count() === 0 && surfaces.pending.count() === 0);
  if (size.width === 0 || size.height === 0 || isEmpty) {
    return null;
  } else {
    return <canvas
      key={`${size.width}x${size.height}`}
      ref={onCanvasRef}
      width={size.width}
      height={size.height}
      className={css('drawRender', {
        position: 'absolute',
        top: 0,
        left: 0,
        width: `${size.width}px`,
        height: `${size.height}px`,
      })}
    />;
  }
};

interface IUseDrawOnCanvas {
  render: (persistedShapes: Shapes, pendingShapes: Shapes) => void;
  reset: () => void;
}

/**
 * Hook that renders persisted and pending shapes on a given canvas
 * rendering context
 *
 * Optmises for rendering only as little as possible. Never rerenders
 * any already persisted and rendered shapes.
 *
 * Before rendering pending shapes, takes a shapshot of the bitmap
 * data. This data is restored when we need to draw peristed shapes
 * again. All rendered pending shapes are thus discarded.
 *
 * Rendering pending shapes behaves similarly: only draws new shapes,
 * until new persisted changes are added. Then it draws new persisted
 * changes, takes a snapshot of the bitmap data and redraws all of
 * pending shapes on top of that.
 */
const useDrawOnCanvas = (ctx: CanvasRenderingContext2D | null): IUseDrawOnCanvas => {
  // Amount of how many persisted shapes were drawn already to avoid
  // redrawing them
  const renderedPersisted = useRef(0);
  // Amount of how many pending shapes were drawn already. This
  // counter is reset every time any new _persisted_ shapes were
  // draw because _pending_ shapes need to be redrawn on top of them.
  const renderedPending = useRef(0);
  const snapshot = useRef<ImageData | null>(null);

  const reset = useCallback(() => {
    renderedPersisted.current = 0;
    renderedPending.current = 0;
    snapshot.current = null;
    if (ctx) {
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    }
  }, [ctx]);

  const render = useCallback((persistedShapes: Shapes, pendingShapes: Shapes) => {
    if (!ctx) {
      return;
    }

    const needToRedrawPersisted = persistedShapes.size > renderedPersisted.current;

    if (needToRedrawPersisted) {
      if (snapshot.current) {
        // If there was a snapshot saved, it means that new pending
        // shapes were draw on top of previously-drawn persisted
        // shapes so we need to restore canvas before drawing
        ctx.putImageData(snapshot.current, 0, 0);
        snapshot.current = null;
      }

      renderShapes(ctx, persistedShapes.skip(renderedPersisted.current));
      renderedPersisted.current = persistedShapes.size;

      // Rendering pending shapes after persisted shapes were drawn
      // always starts from scratch because by restoring the snapshot
      // we discarded all pending shapes drawn so far
      renderedPending.current = 0;
    }

    const needToRedrawPending = pendingShapes.size > renderedPending.current;

    if (needToRedrawPending) {
      if (!snapshot.current) {
        // If snapshot is present, it means that no persisted
        // changes were drawn since last time we created a snapshot
        // so there's no need to snapshot again
        snapshot.current = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height);
      }

      renderShapes(ctx, pendingShapes.skip(renderedPending.current));
      renderedPending.current = pendingShapes.size;
    }
  }, [ctx]);

  return {
    render,
    reset,
  };
};

const renderShapes = (
  ctx: CanvasRenderingContext2D,
  shapes: Shapes,
) => {
  for (const [, shape] of shapes) {
    renderShape(ctx, shape);
  }
};

// Need to disable param reassign rule to be able to set drawing
// context properties
/* eslint-disable no-param-reassign */
const renderShape = (ctx: CanvasRenderingContext2D, shape: Shape) => {
  // Use canvas's state stack to prevent affecting any subsequent
  // drawing calls to affect each other
  ctx.save();

  try {
    switch (shape.type) {
      case TraceType.Pencil:
        ctx.strokeStyle = `rgb(${shape.color.red},${shape.color.green},${shape.color.blue})`;
        break;

      case TraceType.Eraser:
        ctx.globalCompositeOperation = 'destination-out';
        ctx.strokeStyle = '#000';
        break;

      default:
        return;
    }

    ctx.lineWidth = shape.strokeSize;
    // This makes to shapes that are adjacent to each other connect
    // seamlessly by adding a circle around it last point. Could
    // be problematic if we ever try transparency
    ctx.lineCap = 'round';

    // Set transform that translates coords of the shape (coords
    // of the original input) to the coords of the current canvas.
    ctx.setTransform(
      ctx.canvas.width / shape.resolution.width, 0, 0,
      ctx.canvas.height / shape.resolution.height, 0, 0,
    );

    const [init, ...points] = shape.points;

    if (!init) {
      return;
    }

    ctx.beginPath();
    ctx.moveTo(init.x, init.y);

    if (points.length > 0) {
      for (const { x, y } of points) {
        ctx.lineTo(x, y);
      }
    } else {
      // Draw a single point by drawing a line to self
      ctx.lineTo(init.x, init.y);
    }

    ctx.stroke();
  } finally {
    // Undo all of the state changes
    ctx.restore();
  }
};
/* eslint-enable no-param-reassign */

export default DrawRender;
